import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import CustomModal from '../../../../../configuracion/components/customModal';
import { Icon } from 'semantic-ui-react';
import moment from 'moment';
import { formatMoneyWithCurrency } from '../../../../../../utils/formatter';
import JournalEntryRow from './JournalEntryRow';
import ReconcileDocumentSideBar from '../reconcileDocumentSideBar';
import DocumentInfoBar from '../documentInfoBar';
import AddDashedOutlineButton from './AddDashedOutlineButton';
import JournalEntryImportExport from './JournalEntryImportExport';
import InfoBanner from './InfoBanner';
import JournalEntryCostCentersPanel from './JournalEntryCostCentersPanel';
import {
  getCostCenterSaveConfirmState,
  buildEntriesWithCostCenters,
  preserveAccountNames,
  scaleAssignmentForLine,
} from './journalEntryCostCenters';
import {
  draftToFormData,
  formDataToDraft,
  getDraftValidation,
} from './journalEntryExcelParser';
import { useDraftPeriodLocks } from './useDraftPeriodLocks';
import JournalEntryDraftTabs from './JournalEntryDraftTabs';
import {
  buildBulkDocumentDraft,
  buildEmissionEntryDateFromContext,
  buildEmissionEntryDescription,
  createEntryDate,
  resolveDocumentForDescription,
  STRING_DATE_PARSE_FORMATS,
  stringDateToDateInputValue,
} from './emissionEntryDefaults';
import {
  applyAccountingRules,
  getAccountByKey,
  JOURNAL_ENTRY_CURRENCY,
  resolveDocumentEntryAmountClp,
  resolveDocumentEmissionJournalCapClp,
  resolveDocumentEmissionSourceAmountClp,
  getDocumentPayableAmountClp,
  isFacturaCompraWithIvaRetenido,
  resolveTransactionEntryAmountClp,
} from './cheatSheet';
import { CreateEntries, ModifyEntry, GetEntryById } from '../../../../../../graphql/entries';
import { useCheckPeriodLock } from '../../../../../../graphql/accountingPeriod';
import { importAlertState } from '../../../../../../redux/actions/alert';
import { TransactionType } from '../../../../../../utils/enum';
import { useAccounts } from '../../../../../../context/AccountsContext';
import { useRules } from '../../../../../../context/RulesContext';
import { importUpdateTransactions } from '../../../../../../redux/actions/conciliacion';
import { setUpdateDocuments } from '../../../../../../redux/actions/documentostributarios';
import {
  evaluateRuleSaveOffer,
  snapshotRuleBaseline,
} from './journalEntryRuleSave';
import {
  JOURNAL_ENTRY_SAVE_RULE_ACTION,
  clearJournalEntryRuleSaveOffer,
  queueJournalEntryRuleSaveOffer,
} from './journalEntryRuleSaveBridge';
import './CreateJournalEntryModal.scss';

/** Guard UI: session company must match source transaction/document companyId when present. */
function findCrossCompanySourceMessage(
  selectedCompanyId,
  supportItems = [],
  tAccounting,
) {
  if (!selectedCompanyId) return null;
  for (const item of supportItems || []) {
    const cid = item?.companyId;
    if (cid != null && String(cid) !== String(selectedCompanyId)) {
      return tAccounting("journalEntry.otherCompanyWarning");
    }
  }
  return null;
}

const CreateJournalEntryModal = ({
  open,
  onClose,
  onEntryCreated,
  editData,
  bulkDocuments,
  existingEntry,
  duplicateMode = false,
  reverseMode = false,
  modalClassName = '',
}) => {
  const { t } = useTranslation('configuracion');
  const { t: tAccounting } = useTranslation('accounting');
  const dispatch = useDispatch();
  const location = useLocation();
  const { selectedCompany } = useSelector((state) => state.companyList);
  const { accounts, creditCardIdToAccountKeyMap, passwordIdToAccountKeyMap } = useAccounts();
  const { rules } = useRules();
  // Keep refs so the useEffect can always read the latest values without
  // being added to the dependency array (which would cause the form to reset
  // every time context re-renders after an import).
  const accountsRef = useRef(accounts);
  const passwordIdToAccountKeyMapRef = useRef(passwordIdToAccountKeyMap);
  const rulesRef = useRef(rules);
  const creditCardIdToAccountKeyMapRef = useRef(creditCardIdToAccountKeyMap);
  const ruleBaselineRef = useRef(null);
  const prevOpenRef = useRef(false);
  const lastInitializedKeyRef = useRef(null);
  useEffect(() => { accountsRef.current = accounts; }, [accounts]);
  useEffect(() => {
    passwordIdToAccountKeyMapRef.current = passwordIdToAccountKeyMap;
  }, [passwordIdToAccountKeyMap]);
  useEffect(() => { rulesRef.current = rules; }, [rules]);
  useEffect(() => { creditCardIdToAccountKeyMapRef.current = creditCardIdToAccountKeyMap; }, [creditCardIdToAccountKeyMap]);

  const { reexecuteMutation: createEntries } = CreateEntries();
  const { reexecuteMutation: modifyEntry } = ModifyEntry();

  // Edit mode: modifyEntry; copiar/reversar usan createEntries
  const isEditMode = Boolean(existingEntry) && !duplicateMode && !reverseMode;

  const editEntryId = existingEntry
    ? String(existingEntry.id ?? existingEntry._id ?? '')
    : '';
  const { data: entryByIdData, fetching: fetchingEntryById } = GetEntryById({
    companyId: selectedCompany?.id,
    entryId: editEntryId,
    pause: !open || !isEditMode || !editEntryId || !selectedCompany?.id,
  });
  const resolvedExistingEntry = entryByIdData?.entry ?? existingEntry;

  // For honorarios emission entries, editing the gasto account propagates to the
  // counterpart (BH <-> retención). Detect which side this entry is so we can warn
  // the user. The retención side carries a "Retención ... por Pagar" account (2.07.x)
  // or a "Retención" glosa; otherwise it's the honorario (BH) side.
  const honorarioEditSide = useMemo(() => {
    const entry = resolvedExistingEntry;
    if (!entry || entry.emissionNature !== 'honorario') return null;
    const lines = entry.entries || [];
    const isRetencion =
      /retenci[oó]n/i.test(entry.description || '') ||
      lines.some(
        (l) =>
          /^2\.07/.test(String(l.accountKey || '')) ||
          /retenci[oó]n/i.test(String(l.accountName || '')),
      );
    return isRetencion ? 'retencion' : 'honorario';
  }, [resolvedExistingEntry]);

  // Check if this is a free entry (no editData, and not edit mode)
  const isFreeEntry = !editData && !existingEntry;
  // Check if this is bulk mode (multiple documents)
  const isBulkMode = bulkDocuments && bulkDocuments.length > 0;
  
  // State to track if we should show "Fecha adaptada en base a su RCV" message
  const [showRcvDateMessage, setShowRcvDateMessage] = useState(false);
  const [rcvPeriod, setRcvPeriod] = useState(null);
  const [costCentersPanelOpen, setCostCentersPanelOpen] = useState(false);
  const [costCentersPanelKey, setCostCentersPanelKey] = useState(0);
  const [assignedCcCount, setAssignedCcCount] = useState(0);
  const [ccSaveConfirm, setCcSaveConfirm] = useState(null);
  const ccPanelRef = useRef(null);
  const costCentersPanelEngagedRef = useRef(false);
  const skipCcSaveGateRef = useRef(false);
  
  const todayStringDate = moment().format('YYYY-MM-DD');
  const initialEntryDate = createEntryDate(todayStringDate);

  const [formData, setFormData] = useState({
    description: '',
    stringDate: todayStringDate,
    entryDate: initialEntryDate,
    entryType: null, // 'matchEntry', 'emissionEntry', or 'freeEntry'
    sources: [], // Array of { type: 'TRANSACTION' | 'DOCUMENT', id: string }
    transactionId: null, // Deprecated: kept for backward compatibility
    documentId: null, // Deprecated: kept for backward compatibility
    matchId: null,
    entries: [
      {
        ranking: 1,
        account: null,
        debit: '',
        credit: '',
      },
      {
        ranking: 2,
        account: null,
        debit: '',
        credit: '',
      }
    ],
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [importDrafts, setImportDrafts] = useState(null);
  const [activeDraftIndex, setActiveDraftIndex] = useState(0);
  const [bulkDocumentDrafts, setBulkDocumentDrafts] = useState([]);
  const [activeBulkDraftIndex, setActiveBulkDraftIndex] = useState(0);

  const entryDateForCheck = formData.entryDate?.fullDate
    ? formData.entryDate.fullDate.toISOString()
    : null;

  const importPeriodCheckDates = useMemo(() => {
    if (!importDrafts?.length) return [];
    return importDrafts.map((draft, index) =>
      index === activeDraftIndex ? formData.stringDate : draft.stringDate
    );
  }, [importDrafts, activeDraftIndex, formData.stringDate]);

  const { fetching: checkingDraftPeriodLocks, getLockForDate } = useDraftPeriodLocks(
    selectedCompany?.id,
    importPeriodCheckDates,
    Boolean(open && importDrafts?.length)
  );

  const bulkPeriodCheckDates = useMemo(() => {
    if (!isBulkMode || !bulkDocumentDrafts?.length) return [];
    return bulkDocumentDrafts.map((draft) => draft.stringDate);
  }, [isBulkMode, bulkDocumentDrafts]);

  const { fetching: checkingBulkPeriodLocks, getLockForDate: getBulkLockForDate } = useDraftPeriodLocks(
    selectedCompany?.id,
    bulkPeriodCheckDates,
    Boolean(open && isBulkMode && bulkDocumentDrafts?.length)
  );

  const [{ data: periodLockData, fetching: checkingPeriodLock }] = useCheckPeriodLock(
    open && !importDrafts?.length && !isBulkMode ? selectedCompany?.id : null,
    open && !importDrafts?.length && !isBulkMode ? entryDateForCheck : null,
    'ENTRIES'
  );

  const activePeriodLock = importDrafts?.length
    ? getLockForDate(formData.stringDate)
    : periodLockData?.checkPeriodLock ?? { isLocked: false, message: '' };

  const isPeriodLocked = activePeriodLock.isLocked || false;
  const periodLockMessage = activePeriodLock.message || '';

  // Pre-fill form when editing an existing entry (or when editData / bulkDocuments provided)
  // Only re-run init when modal opens or when the logical context (which entry/doc) changes.
  useEffect(() => {
    if (!open) {
      lastInitializedKeyRef.current = null;
      prevOpenRef.current = false;
      ruleBaselineRef.current = null;
      setImportDrafts(null);
      setActiveDraftIndex(0);
      setBulkDocumentDrafts([]);
      setActiveBulkDraftIndex(0);
      return;
    }

    const existingEntryId = existingEntry ? String(existingEntry.id ?? existingEntry._id ?? '') : null;
    const bulkKey =
      isBulkMode && bulkDocuments?.length
        ? bulkDocuments
            .map((d) => String(d?.id ?? d?._id ?? ''))
            .filter(Boolean)
            .sort()
            .join(',')
        : null;
    let editDataKey = null;
    if (editData) {
      const sources = editData.sources || [];
      const sourcesKey = sources
        .map((s) => String(s?.id ?? ''))
        .filter(Boolean)
        .sort()
        .join(',');
      if (sourcesKey) {
        editDataKey = sourcesKey;
      } else {
        const supportTx = editData.support?.transactions?.[0];
        const supportDoc = editData.support?.documents?.[0];
        const supportKeyParts = [
          supportTx && String(supportTx.id ?? supportTx._id ?? ''),
          supportDoc && String(supportDoc.id ?? supportDoc._id ?? ''),
        ].filter(Boolean);
        editDataKey = supportKeyParts.sort().join(',') || null;
      }
    }
    const existingContextPrefix = existingEntryId
      ? (reverseMode ? 'rev' : duplicateMode ? 'dup' : 'edit')
      : null;
    const contextKey = existingContextPrefix
      ? `${existingContextPrefix}-${existingEntryId}`
      : bulkKey || editDataKey || 'free';

    if (prevOpenRef.current && lastInitializedKeyRef.current === contextKey) {
      return;
    }
    lastInitializedKeyRef.current = contextKey;
    prevOpenRef.current = true;

    if (open && existingEntry) {
      const rawStringDate = existingEntry.stringDate || moment().format('YYYY-MM-DD');
      const stringDateMoment = moment(rawStringDate, STRING_DATE_PARSE_FORMATS, true);
      const stringDate = stringDateMoment.isValid()
        ? stringDateMoment.format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD');
      const entryDate = existingEntry.entryDate?.fullDate
        ? createEntryDate(moment(existingEntry.entryDate.fullDate).format('YYYY-MM-DD'))
        : createEntryDate(stringDate);
      const mappedEntries = (existingEntry.entries || []).map((row) => {
        const debitStr = row.debit != null && row.debit !== '' ? String(row.debit) : '';
        const creditStr = row.credit != null && row.credit !== '' ? String(row.credit) : '';
        if (reverseMode) {
          return {
            ranking: row.ranking,
            account: row.accountKey || null,
            debit: creditStr,
            credit: debitStr,
          };
        }
        return {
          ranking: row.ranking,
          account: row.accountKey || null,
          debit: debitStr,
          credit: creditStr,
        };
      });
      const entries = mappedEntries.length >= 2
        ? mappedEntries
        : [
            ...mappedEntries,
            ...Array.from({ length: Math.max(0, 2 - mappedEntries.length) }, (_, i) => ({
              ranking: mappedEntries.length + i + 1,
              account: null,
              debit: '',
              credit: '',
            })),
          ];
      const originalDescTrim = (existingEntry.description || '').trim();
      const description = reverseMode
        ? `Reversado de: ${originalDescTrim || `asiento #${existingEntry.entryNumber}`}`
        : (existingEntry.description || '');
      setFormData({
        description,
        stringDate,
        entryDate,
        entryType: existingEntry.entryType || null,
        sources: existingEntry.sources || [],
        transactionId: existingEntry.transactionId || null,
        documentId: existingEntry.documentId || null,
        matchId: existingEntry.matchId || null,
        entries,
      });
      setShowRcvDateMessage(false);
      setRcvPeriod(null);
      return;
    }

    // Handle bulk mode separately
    if (open && isBulkMode && bulkDocuments && bulkDocuments.length > 0) {
      const firstDoc = bulkDocuments[0];
      
      // Calculate sum of all documents' amounts
      const totalAmount = bulkDocuments.reduce(
        (sum, doc) => sum + resolveDocumentEntryAmountClp(doc),
        0,
      );
      
      // Calculate sum of taxAmount, netAmount, otherTaxes across all documents
      const totalTaxAmount = bulkDocuments.reduce((sum, doc) => {
        return sum + (doc.meta?.taxAmount || 0);
      }, 0);
      
      const totalNetAmount = bulkDocuments.reduce((sum, doc) => {
        return sum + (doc.meta?.netAmount || 0);
      }, 0);
      
      // Sum otherTaxes - need to preserve array structure but sum values
      // Group by tax type and sum values
      const otherTaxesMap = new Map();
      bulkDocuments.forEach(doc => {
        const otherTaxes = doc.meta?.otherTaxes || [];
        otherTaxes.forEach(tax => {
          const key = tax.type || tax.name || 'other';
          const existing = otherTaxesMap.get(key) || { ...tax, value: 0 };
          existing.value = (existing.value || 0) + (tax.value || 0);
          otherTaxesMap.set(key, existing);
        });
      });
      const totalOtherTaxesArray = Array.from(otherTaxesMap.values());
      
      // Create a synthetic document with summed amounts for rule application
      const syntheticDoc = {
        ...firstDoc,
        amount: totalAmount,
        localValue: totalAmount,
        meta: {
          ...firstDoc.meta,
          taxAmount: totalTaxAmount,
          netAmount: totalNetAmount,
          otherTaxes: totalOtherTaxesArray.length > 0 ? totalOtherTaxesArray : (firstDoc.meta?.otherTaxes || []),
          originalAmount: totalAmount
        }
      };
      
      // Apply accounting rules using the synthetic document with summed amounts
      const editDataForRules = {
        originType: firstDoc.documentType,
        localAmount: totalAmount,
        sources: [{ type: 'DOCUMENT', id: firstDoc._id || firstDoc.id }],
        support: { documents: [syntheticDoc], transactions: [] },
        origin: 'DOCUMENT',
        type: firstDoc.documentType === 'OBLIGATION' ? TransactionType.SPEND : TransactionType.RECIEVED
      };
      
      const defaultEntries = applyAccountingRules(
        editDataForRules,
        selectedCompany,
        accountsRef.current,
        rulesRef.current,
        passwordIdToAccountKeyMapRef.current,
        creditCardIdToAccountKeyMapRef.current,
      );

      // Get default date from first document
      let defaultStringDate = moment().format('DD/MM/YYYY');
      if (firstDoc.documentDate) {
        const documentDateMoment = moment(firstDoc.documentDate);
        defaultStringDate = documentDateMoment.format('DD/MM/YYYY');
      }
      const defaultEntryDate = createEntryDate(defaultStringDate);
      
      const isFromConciliacion = location.pathname === '/conciliacion';
      const drafts = bulkDocuments.map((doc) =>
        buildBulkDocumentDraft(doc, {
          applyRcvLogic: true,
          isFromConciliacion,
        })
      );
      setBulkDocumentDrafts(drafts);
      setActiveBulkDraftIndex(0);

      setFormData(prev => ({
        ...prev,
        description: '',
        stringDate: '',
        entryDate: createEntryDate(moment().format('YYYY-MM-DD')),
        entryType: 'emissionEntry',
        sources: bulkDocuments.map(doc => ({ type: 'DOCUMENT', id: doc._id || doc.id })),
        transactionId: null,
        documentId: null,
        matchId: null,
        entries: defaultEntries.entries || prev.entries,
      }));
      
      setShowRcvDateMessage(false);
      setRcvPeriod(null);
      return;
    }
    
    if (open && editData) {
      // Use explicit sources array if provided, otherwise fall back to inferring from support structure
      let sources = editData.sources || [];
      
      // If no explicit sources, build from support structure (backward compatibility)
      if (sources.length === 0) {
        const transaction = editData.support?.transactions?.[0];
        const document = editData.support?.documents?.[0];
        
        // Check if we have explicit source types in support structure
        // If sourceType is specified, use it; otherwise infer from context
        if (transaction) {
          // Check if transaction has explicit sourceType, otherwise check if it's actually a document
          let transactionType = transaction.sourceType;
          if (!transactionType) {
            // Infer: if it has documentType, it's a DOCUMENT; otherwise TRANSACTION
            transactionType = transaction.documentType !== undefined ? 'DOCUMENT' : 'TRANSACTION';
          }
          const transactionId = transaction?.id || transaction?._id || null;
          if (transactionId) {
            sources.push({ type: transactionType, id: transactionId });
          }
        }
        if (document) {
          // Check if document has explicit sourceType, otherwise check if it's actually a transaction
          let documentType = document.sourceType;
          if (!documentType) {
            // Infer: if it has documentType property, it's a DOCUMENT; otherwise might be TRANSACTION
            documentType = document.documentType !== undefined ? 'DOCUMENT' : 'TRANSACTION';
          }
          const documentId = document?.id || document?._id || null;
          if (documentId) {
            sources.push({ type: documentType, id: documentId });
          }
        }
      }
      
      // Determine entry type: "matchEntry" if multiple sources exist, otherwise "emissionEntry"
      const entryType = sources.length > 1 ? 'matchEntry' : 'emissionEntry';
      
      // Extract IDs for backward compatibility (keep old fields)
      const transactionSource = sources.find(s => s.type === 'TRANSACTION');
      const documentSource = sources.find(s => s.type === 'DOCUMENT');
      const transactionId = transactionSource?.id || null;
      const documentId = documentSource?.id || null;
      
      // Also get the actual objects for date/type extraction
      const transaction = editData.support?.transactions?.[0];
      const document = editData.support?.documents?.[0];
      
      const documentForDescription = resolveDocumentForDescription({
        entryType,
        document,
        supportDocuments: editData.support?.documents,
        originType: editData.originType,
      });

      const descriptionText = buildEmissionEntryDescription({
        entryType,
        document,
        documentForDescription,
        transaction,
        fallbackDescription: editData.description || '',
      });

      // Extract matchId for matchEntry type
      // matchId can be found in:
      // 1. Directly in editData.matchId (passed from MatchRow)
      // 2. Transaction's matchs array where docId matches documentId
      // 3. Document's meta.matchs.inMatch where id matches transactionId
      let matchId = editData?.matchId || null;
      if (entryType === 'matchEntry' && !matchId && transactionId && documentId) {
        // Normalize IDs to strings for comparison
        const normalizedDocumentId = String(documentId);
        const normalizedTransactionId = String(transactionId);
        
        // Try to find matchId from transaction's matchs
        if (transaction?.matchs && Array.isArray(transaction.matchs)) {
          const transactionMatch = transaction.matchs.find(m => {
            const matchDocId = String(m.docId || '');
            return matchDocId === normalizedDocumentId || 
                   matchDocId === String(document?._id || '') || 
                   matchDocId === String(document?.id || '');
          });
          if (transactionMatch?.matchId) {
            matchId = String(transactionMatch.matchId);
          }
        }
        
        // If not found, try to find from document's meta.matchs.inMatch
        if (!matchId && document?.meta?.matchs?.inMatch && Array.isArray(document.meta.matchs.inMatch)) {
          const documentMatch = document.meta.matchs.inMatch.find(m => {
            const matchId = String(m.id || '');
            return matchId === normalizedTransactionId || 
                   matchId === String(transaction?._id || '') || 
                   matchId === String(transaction?.id || '');
          });
          if (documentMatch?.matchId) {
            matchId = String(documentMatch.matchId);
          }
        }
        
        // Also check if matchId is directly on the match object (when match is passed as document)
        if (!matchId && document?.matchId) {
          matchId = String(document.matchId);
        }
      }

      // When two DOCUMENT sources (document-document match), derive matchId from support.documents
      if (entryType === 'matchEntry' && !matchId && editData?.sources?.length === 2 && editData?.support?.documents?.length >= 2) {
        const docSources = editData.sources.filter(s => s.type === 'DOCUMENT');
        if (docSources.length === 2) {
          const docA = editData.support.documents[0];
          const docB = editData.support.documents[1];
          const idA = String(docA?.id ?? docA?._id ?? '');
          const idB = String(docB?.id ?? docB?._id ?? '');
          const inOrOut = (doc, otherId) => {
            const inMatch = doc?.meta?.matchs?.inMatch || [];
            const outMatch = doc?.meta?.matchs?.outMatch || [];
            const found = inMatch.find(m => String(m.id || '') === otherId || String(m.docId || '') === otherId)
              || outMatch.find(m => String(m.id || '') === otherId || String(m.docId || '') === otherId);
            return found?.matchId;
          };
          matchId = inOrOut(docA, idB) || inOrOut(docB, idA);
          if (matchId) matchId = String(matchId);
        }
      }

      // Apply accounting rules to get default entries
      // For transactions, we need to get the type from the transaction in support
      // For documents, try to infer transaction type from document properties or matches
      let transactionType = transaction?.type || editData.type;
      
      // If no transaction type and we have a document, try to infer it
      if (!transactionType && document) {
        // Check if document has matches with transactions that have a type
        if (document?.meta?.matchs?.inMatch && Array.isArray(document.meta.matchs.inMatch)) {
          // Try to find a matched transaction with a type
          const matchedTransaction = document.meta.matchs.inMatch.find(m => m.type);
          if (matchedTransaction?.type) {
            transactionType = matchedTransaction.type;
          }
        }
        
        // If still no type, check document properties to infer
        // Documents like invoices/contracts are typically income (RECIEVED)
        // Documents like obligations/bills are typically expenses (SPEND)
        if (!transactionType) {
          // Default based on document type - can be customized based on business logic
          // For now, default to RECIEVED for sales documents (TAX_DOCUMENT, CREDIT)
          // and SPEND for obligations
          const documentType = document.documentType || editData.originType;
          if (documentType === 'OBLIGATION') {
            transactionType = TransactionType.SPEND; // Obligations are typically expenses
          } else {
            // TAX_DOCUMENT, CREDIT, and other sales documents are typically income
            transactionType = TransactionType.RECIEVED;
          }
        }
      }
      
      const isFromConciliacion = location.pathname === '/conciliacion';
      const { stringDate: defaultStringDate, rcvPeriod: defaultRcvPeriod } =
        buildEmissionEntryDateFromContext({
          entryType,
          document,
          transaction,
          documentSource,
          supportDocuments: editData.support?.documents,
          originType: editData.originType,
          isFromConciliacion,
        });

      setShowRcvDateMessage(Boolean(defaultRcvPeriod));
      setRcvPeriod(defaultRcvPeriod);

      const defaultEntryDate = createEntryDate(defaultStringDate);
      
      // Create editData with transaction type for rule application
      const editDataForRules = {
        ...editData,
        type: transactionType,
      };
      
      const defaultEntries = applyAccountingRules(
        editDataForRules,
        selectedCompany,
        accountsRef.current,
        rulesRef.current,
        passwordIdToAccountKeyMapRef.current,
        creditCardIdToAccountKeyMapRef.current,
      );

      ruleBaselineRef.current = snapshotRuleBaseline({
        entryType,
        editData: editDataForRules,
        defaultEntries: defaultEntries?.entries || [],
        rules: rulesRef.current,
        accounts: accountsRef.current,
        passwordIdToAccountKeyMap: passwordIdToAccountKeyMapRef.current,
      });
      
      setFormData(prev => ({
        ...prev,
        description: descriptionText,
        stringDate: defaultStringDate,
        entryDate: defaultEntryDate,
        entryType: entryType,
        sources: sources, // Store explicit sources array
        transactionId: transactionId, // Keep for backward compatibility
        documentId: documentId, // Keep for backward compatibility
        matchId: matchId,
        entries: defaultEntries.entries || prev.entries,
      }));
    } else if (open && !editData) {
      setImportDrafts(null);
      setActiveDraftIndex(0);
      const todayStringDate = moment().format('YYYY-MM-DD');
      const initialEntryDate = createEntryDate(todayStringDate);
      const freeEntryDefaults = [
        {
          ranking: 1,
          account: null,
          debit: '',
          credit: '',
        },
        {
          ranking: 2,
          account: null,
          debit: '',
          credit: '',
        },
      ];
      ruleBaselineRef.current = snapshotRuleBaseline({
        entryType: 'freeEntry',
        editData: null,
        defaultEntries: freeEntryDefaults,
        rules: rulesRef.current,
        accounts: accountsRef.current,
        passwordIdToAccountKeyMap: passwordIdToAccountKeyMapRef.current,
      });
      setFormData({
        description: '',
        stringDate: todayStringDate,
        entryDate: initialEntryDate,
        entryType: 'freeEntry',
        sources: [],
        transactionId: null,
        documentId: null,
        matchId: null,
        entries: freeEntryDefaults,
      });
      setShowRcvDateMessage(false);
      setRcvPeriod(null);
    }
    prevOpenRef.current = open;
  // Note: accounts and rules are intentionally excluded — they are accessed via
  // refs (accountsRef/rulesRef) to prevent the effect from re-running (and
  // resetting the form) whenever the context re-renders after an import.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editData, existingEntry, duplicateMode, reverseMode, location.pathname, selectedCompany, isBulkMode, bulkDocuments]);

  const prevOpenForCcRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenForCcRef.current) {
      setCostCentersPanelOpen(false);
      setAssignedCcCount(0);
      costCentersPanelEngagedRef.current = false;
      setCcSaveConfirm(null);
      skipCcSaveGateRef.current = false;
    }
    if (!open && prevOpenForCcRef.current) {
      setCostCentersPanelKey((k) => k + 1);
    }
    prevOpenForCcRef.current = open;
  }, [open]);

  useEffect(() => {
    if (costCentersPanelOpen) {
      costCentersPanelEngagedRef.current = true;
    }
  }, [costCentersPanelOpen]);

  const handleImportEntries = ({ drafts, missingAccountKeys }) => {
    if (!drafts?.length) return;

    setImportDrafts(drafts);
    setActiveDraftIndex(0);
    setErrorMessage('');

    setFormData((prev) =>
      draftToFormData(drafts[0], {
        ...prev,
        entryType: 'freeEntry',
      }),
    );
    ruleBaselineRef.current = snapshotRuleBaseline({
      entryType: 'freeEntry',
      editData: null,
      defaultEntries: drafts[0]?.entries || [],
      rules: rulesRef.current,
      accounts: accountsRef.current,
      passwordIdToAccountKeyMap: passwordIdToAccountKeyMapRef.current,
    });

    // Cuentas del Excel que no existen en el plan de cuentas: bloquean la
    // creación (el asiento queda inválido por "Faltan cuentas") y se listan
    // explícitamente para que el usuario sepa qué códigos corregir.
    if (missingAccountKeys?.length) {
      const shown = missingAccountKeys.slice(0, 10).join(', ');
      const extra =
        missingAccountKeys.length > 10
          ? ` y ${missingAccountKeys.length - 10} más`
          : '';
      setErrorMessage(
        `Faltan cuentas: los siguientes códigos no existen en el plan de cuentas: ${shown}${extra}. ` +
          tAccounting("journalEntry.fixBeforeCreating")
      );
      return;
    }

    const invalidCount = drafts.filter((d) => !getDraftValidation(d).isValid).length;
    if (invalidCount > 0) {
      setErrorMessage(
        `Se importaron ${drafts.length} asiento${drafts.length === 1 ? '' : 's'}. ` +
          `${invalidCount} requiere${invalidCount === 1 ? '' : 'n'} corrección (desbalanceado o sin líneas válidas).`
      );
    } else {
      setErrorMessage('');
    }
  };

  const handleSelectDraft = (index) => {
    if (!importDrafts || index === activeDraftIndex) return;

    setImportDrafts((prev) => {
      const copy = [...prev];
      copy[activeDraftIndex] = formDataToDraft(formData);
      const nextForm = draftToFormData(copy[index], {
        ...formData,
        entryType: 'freeEntry',
      });
      setFormData(nextForm);
      return copy;
    });
    setActiveDraftIndex(index);
    setErrorMessage('');
  };

  const buildFreeEntryRowsForBackend = (entries) =>
    entries
      .filter((entry) => {
        if (!entry.account) return false;
        return parseFloat(entry.debit) || parseFloat(entry.credit);
      })
      .map((entry) => {
        let accountKey = '';
        let accountName = '';

        if (typeof entry.account === 'string') {
          accountKey = entry.account;
          const accountObj = getAccountByKey(entry.account, accounts);
          accountName = accountObj?.name || entry.account;
        } else if (entry.account && typeof entry.account === 'object') {
          accountKey = entry.account.key || '';
          accountName = entry.account.name || '';
        }

        return {
          ranking: entry.ranking,
          accountKey,
          accountName,
          debit: parseFloat(entry.debit) || 0,
          credit: parseFloat(entry.credit) || 0,
          rut: entry.rut || undefined,
          businessName: entry.businessName || undefined,
        };
      });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDescriptionChange = (e) => {
    handleInputChange('description', e.target.value);
  };

  const handleDateChange = (e) => {
    // Keep raw input value (including '') so clearing/typing behaves like
    // documentos_tributarios date filters — never force "today" mid-edit.
    const dateValue = e.target.value; // YYYY-MM-DD or '' from <input type="date" />
    const parsed = dateValue ? moment(dateValue, 'YYYY-MM-DD', true) : null;
    const isValid = Boolean(parsed?.isValid());
    setFormData((prev) => ({
      ...prev,
      stringDate: dateValue,
      entryDate: isValid ? createEntryDate(dateValue) : prev.entryDate,
    }));
    // Hide RCV message when user manually changes the date
    setShowRcvDateMessage(false);
    setRcvPeriod(null);
  };

  const handleSelectBulkDraft = (index) => {
    if (index === activeBulkDraftIndex) return;
    setActiveBulkDraftIndex(index);
  };

  const handleBulkDraftDescriptionChange = (value) => {
    setBulkDocumentDrafts((prev) =>
      prev.map((draft, i) =>
        i === activeBulkDraftIndex ? { ...draft, description: value } : draft
      )
    );
  };

  const handleBulkDraftDateChange = (e) => {
    const dateValue = e.target.value; // YYYY-MM-DD or '' from <input type="date" />
    const parsed = dateValue ? moment(dateValue, 'YYYY-MM-DD', true) : null;
    const isValid = Boolean(parsed?.isValid());
    setBulkDocumentDrafts((prev) =>
      prev.map((draft, i) => {
        if (i !== activeBulkDraftIndex) return draft;
        return {
          ...draft,
          stringDate: dateValue,
          entryDate: isValid ? createEntryDate(dateValue) : draft.entryDate,
          rcvPeriod: null,
        };
      })
    );
  };

  const handleEntryAccountChange = (entryRanking, account) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.map(entry =>
        entry.ranking === entryRanking ? { ...entry, account } : entry
      )
    }));
  };

  const handleEntryDebitChange = (entryRanking, debit) => {
    if (showCostCentersFeature && ccPanelRef.current?.hasSelection?.()) {
      const state = ccPanelRef.current.getAssignmentState();
      const scaledMap = scaleAssignmentForLine(
        entryRanking,
        debit === '' ? 0 : debit,
        '',
        state.byAccountMap,
      );
      ccPanelRef.current.loadAssignment?.({
        ...state,
        byAccountMap: scaledMap,
        hasAny: true,
      });
    }
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.map(entry => {
        if (entry.ranking === entryRanking) {
          const debitValue = debit === '' ? '' : (isNaN(parseFloat(debit)) ? '0' : debit);
          return { ...entry, debit: debitValue, credit: '' };
        }
        return entry;
      })
    }));
  };

  const handleEntryCreditChange = (entryRanking, credit) => {
    if (showCostCentersFeature && ccPanelRef.current?.hasSelection?.()) {
      const state = ccPanelRef.current.getAssignmentState();
      const scaledMap = scaleAssignmentForLine(
        entryRanking,
        '',
        credit === '' ? 0 : credit,
        state.byAccountMap,
      );
      ccPanelRef.current.loadAssignment?.({
        ...state,
        byAccountMap: scaledMap,
        hasAny: true,
      });
    }
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.map(entry => {
        if (entry.ranking === entryRanking) {
          const creditValue = credit === '' ? '' : (isNaN(parseFloat(credit)) ? '0' : credit);
          return { ...entry, credit: creditValue, debit: '' };
        }
        return entry;
      })
    }));
  };

  const handleInvertEntryAmounts = () => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.map((entry) => ({
        ...entry,
        debit: entry.credit != null && entry.credit !== '' ? String(entry.credit) : '',
        credit: entry.debit != null && entry.debit !== '' ? String(entry.debit) : '',
      })),
    }));
  };

  const handleAddEntry = () => {
    setFormData(prev => {
      // Get the next ranking number
      const maxRanking = prev.entries.length > 0 
        ? Math.max(...prev.entries.map(entry => entry.ranking))
        : 0;
      const nextRanking = maxRanking + 1;
      
      return {
        ...prev,
        entries: [
          ...prev.entries,
          {
            ranking: nextRanking,
            account: null,
            debit: '',
            credit: '',
          }
        ]
      };
    });
  };

  const handleRemoveEntry = (entryRanking) => {
    setFormData(prev => {
      // Always keep at least 2 entries
      if (prev.entries.length <= 2) {
        return prev;
      }
      return {
        ...prev,
        entries: prev.entries.filter(entry => entry.ranking !== entryRanking)
      };
    });
  };

  const finishEntryCreateFlow = (finalFormData, successAlert, offerContext = {}) => {
    // Evaluate BEFORE onEntryCreated/onClose: Auditor clears journalEntryDocumentId in
    // onEntryCreated, which sets open=false and the init effect nulls ruleBaselineRef.
    const offer = evaluateRuleSaveOffer({
      baseline: offerContext.baseline ?? ruleBaselineRef.current,
      finalEntries: finalFormData?.entries || [],
      editData: offerContext.editData !== undefined ? offerContext.editData : editData,
      accounts: accountsRef.current,
      passwordIdToAccountKeyMap: passwordIdToAccountKeyMapRef.current,
      isEditMode,
      duplicateMode,
      reverseMode,
      isBulkMode,
      importDraftsCount: importDrafts?.length || 0,
    });

    if (offer) {
      queueJournalEntryRuleSaveOffer(offer);
    } else {
      clearJournalEntryRuleSaveOffer();
    }

    dispatch(
      importAlertState({
        state: 'success',
        content: successAlert.content,
        concept: successAlert.concept,
        duration: offer ? 12 : (successAlert.duration ?? 4),
        time: new Date(),
        ...(offer
          ? {
              actionLabel: tAccounting('journalEntry.saveRuleToastAction'),
              actionId: JOURNAL_ENTRY_SAVE_RULE_ACTION,
            }
          : {}),
      }),
    );

    if (onEntryCreated) {
      onEntryCreated();
    }
    onClose();
  };

  const handleConfirm = async () => {
    // Freeze offer inputs before awaits / parent close side-effects.
    const ruleSaveOfferContext = {
      baseline: ruleBaselineRef.current,
      editData,
    };

    const hasInvalidDraftDates = isBulkMode
      ? (bulkDocumentDrafts || []).some(
          (d) => !d?.stringDate || !moment(d.stringDate, 'YYYY-MM-DD', true).isValid(),
        )
      : !formData.stringDate || !moment(formData.stringDate, 'YYYY-MM-DD', true).isValid();

    if (hasInvalidDraftDates) {
      setErrorMessage(tAccounting("journalEntry.invalidEntryDate"));
      return;
    }

    // Compute expected amount for validation (same logic as getExpectedAmount)
    let expectedAmountForValidation = null;
    if (editData && formData.entryType !== 'freeEntry') {
      if (isBulkMode && bulkDocuments?.length > 0) {
        expectedAmountForValidation = bulkDocuments.reduce(
          (sum, doc) => sum + resolveDocumentEntryAmountClp(doc),
          0
        );
      } else if (formData.entryType === 'emissionEntry' && editData?.support) {
        const doc = editData.support?.documents?.[0];
        const tx = editData.support?.transactions?.[0];
        expectedAmountForValidation = doc
          ? resolveDocumentEmissionSourceAmountClp(doc)
          : tx
            ? resolveTransactionEntryAmountClp(tx)
            : editData.localAmount ?? 0;
      } else if (formData.entryType === 'matchEntry') {
        expectedAmountForValidation = editData.localAmount ?? 0;
      }
    }
    const isEmissionEntryWithZeroAmount =
      formData.entryType === 'emissionEntry' &&
      expectedAmountForValidation !== null &&
      Math.abs(expectedAmountForValidation) < 0.01;

    const debitTotalForValidation = formData.entries.reduce(
      (sum, e) => sum + (parseFloat(e.debit) || 0),
      0
    );
    const creditTotalForValidation = formData.entries.reduce(
      (sum, e) => sum + (parseFloat(e.credit) || 0),
      0
    );

    const existingEntryTotalMaxForConfirm =
      existingEntry && (existingEntry.entries || []).length > 0
        ? Math.max(
            (existingEntry.entries || []).reduce((s, r) => s + (r.debit ?? 0), 0),
            (existingEntry.entries || []).reduce((s, r) => s + (r.credit ?? 0), 0)
          )
        : 0;
    const isEditingZeroAmountEmissionEntryConfirm =
      (isEditMode || duplicateMode) &&
      existingEntry &&
      formData.entryType === 'emissionEntry' &&
      existingEntryTotalMaxForConfirm < 0.01;

    const hasValidEntries =
      formData.entries.some(
        (entry) => entry.account && (entry.debit || entry.credit)
      ) ||
      (isEmissionEntryWithZeroAmount &&
        formData.entries.filter((e) => e.account).length >= 2 &&
        debitTotalForValidation < 0.01 &&
        creditTotalForValidation < 0.01) ||
      (isEditingZeroAmountEmissionEntryConfirm &&
        formData.entries.filter((e) => e.account).length >= 2 &&
        debitTotalForValidation < 0.01 &&
        creditTotalForValidation < 0.01);

    if (!hasValidEntries) {
      setErrorMessage(tAccounting("journalEntry.addAtLeastOneLine"));
      return;
    }

    // Validate that all entries with amounts have accounts
    const entriesWithoutAccounts = formData.entries.filter(entry => 
      (entry.debit || entry.credit) && !entry.account
    );

    if (entriesWithoutAccounts.length > 0) {
      setErrorMessage(tAccounting("journalEntry.selectAccountAllLines"));
      return;
    }

    if (duplicateMode && formData.entryType !== 'freeEntry') {
      setErrorMessage('Solo se pueden duplicar asientos libres.');
      return;
    }

    if (reverseMode && formData.entryType !== 'freeEntry') {
      setErrorMessage('Solo se pueden reversar asientos libres.');
      return;
    }

    if (!skipCcSaveGateRef.current) {
      if (
        showCostCentersFeature &&
        costCentersPanelEngagedRef.current &&
        ccPanelRef.current
      ) {
        const assignment = ccPanelRef.current.getAssignmentState();
        const ccGate = getCostCenterSaveConfirmState({
          ...assignment,
          journalEntries: formData.entries,
        });
        if (ccGate.blocking) {
          setCcSaveConfirm({ type: 'blocking', message: ccGate.message });
          return;
        }
        if (ccGate.needsConfirm) {
          setCcSaveConfirm({ type: 'confirm', message: ccGate.message });
          return;
        }
      }
    }
    skipCcSaveGateRef.current = false;

    setIsLoading(true);
    setErrorMessage('');

    try {
      // Edit mode: update existing entry via modifyEntry
      if (isEditMode && existingEntry) {
        const entryId = existingEntry.id || existingEntry._id;
        const existingEntryTotalMaxEdit =
          (existingEntry.entries || []).length > 0
            ? Math.max(
                (existingEntry.entries || []).reduce((s, r) => s + (r.debit ?? 0), 0),
                (existingEntry.entries || []).reduce((s, r) => s + (r.credit ?? 0), 0)
              )
            : 0;
        const isEditingZeroAmountEmissionEntryEdit =
          formData.entryType === 'emissionEntry' && existingEntryTotalMaxEdit < 0.01;

        let entriesForBackend = formData.entries
          .filter(
            (e) =>
              e.account &&
              ((parseFloat(e.debit) || parseFloat(e.credit)) || isEditingZeroAmountEmissionEntryEdit)
          )
          .map((e) => {
            let accountKey = '';
            let accountName = '';
            if (typeof e.account === 'string') {
              accountKey = e.account;
              const accountObj = getAccountByKey(e.account, accounts);
              accountName = accountObj?.name || e.account;
            } else if (e.account && typeof e.account === 'object') {
              accountKey = e.account.key || '';
              accountName = e.account.name || '';
            }
            return {
              ranking: e.ranking,
              accountKey,
              accountName,
              debit: parseFloat(e.debit) || 0,
              credit: parseFloat(e.credit) || 0,
            };
          });

        if (showCostCentersFeature && ccPanelRef.current) {
          const assignment = ccPanelRef.current.getAssignmentState();
          const catalogList = ccPanelRef.current.getCatalogList?.() || [];
          try {
            const built = buildEntriesWithCostCenters(
              formData.entries,
              assignment,
              catalogList,
            );
            if (built.applyingCostCenters) {
              entriesForBackend = preserveAccountNames(built.entries, {
                accounts,
                fallbackLines: existingEntry.entries,
              }).filter(
                (e) =>
                  e.accountKey &&
                  ((e.debit || e.credit) || isEditingZeroAmountEmissionEntryEdit),
              );
            }
          } catch (ccErr) {
            setIsLoading(false);
            setErrorMessage(ccErr.message || tAccounting("journalEntry.costCenterError"));
            return;
          }
        }
        const input = [
          {
            id: String(entryId),
            fields: [
              { key: 'description', value: formData.description, oldValue: existingEntry.description },
              { key: 'stringDate', value: formData.stringDate, oldValue: existingEntry.stringDate },
              {
                key: 'entryDate',
                value: JSON.stringify({
                  day: formData.entryDate.day,
                  month: formData.entryDate.month,
                  year: formData.entryDate.year,
                  fullDate: formData.entryDate.fullDate,
                }),
                oldValue: existingEntry.entryDate ? JSON.stringify(existingEntry.entryDate) : undefined,
              },
              {
                key: 'entries',
                value: JSON.stringify(entriesForBackend),
                oldValue: existingEntry.entries ? JSON.stringify(existingEntry.entries) : undefined,
              },
            ],
          },
        ];
        const result = await modifyEntry({
          companyId: selectedCompany.id,
          input,
          action: 'UPDATE',
        });
        if (result.error) {
          throw new Error(result.error.message);
        }
        dispatch(
          importAlertState({
            state: 'success',
            content: tAccounting('alerts.updatedContent', { number: existingEntry.entryNumber }),
            concept: tAccounting('alerts.updatedConcept'),
            duration: 4,
            time: new Date(),
          })
        );
        if (onEntryCreated) {
          onEntryCreated();
        }
        onClose();
        return;
      }

      const currency = JOURNAL_ENTRY_CURRENCY;

      // Handle bulk mode: create one entry per document
      if (isBulkMode && bulkDocuments && bulkDocuments.length > 0) {
        // Get template accounts from formData.entries (user's edited accounts)
        const templateAccounts = formData.entries.map(entry => {
          let accountKey = '';
          let accountName = '';
          
          if (typeof entry.account === 'string') {
            accountKey = entry.account;
            const accountObj = getAccountByKey(entry.account, accounts);
            accountName = accountObj?.name || entry.account;
          } else if (entry.account && typeof entry.account === 'object') {
            accountKey = entry.account.key || '';
            accountName = entry.account.name || '';
          }
          
          return { accountKey, accountName };
        });

        // Create one entry per document
        const entryInputs = bulkDocuments.map((doc, index) => {
          const draft = bulkDocumentDrafts[index];
          // Normalize document ID - prioritize _id to match TablaFila which uses data._id in URL
          // reconcileContabilizar uses: currentDocumentData?.id || currentDocumentData?._id
          // So we use _id first to ensure consistency
          const documentId = String(doc._id || doc.id);
          
          // Apply accounting rules for THIS specific document to get correct amount structure
          const editDataForDoc = {
            originType: doc.documentType,
            localAmount: resolveDocumentEntryAmountClp(doc),
            sources: [{ type: 'DOCUMENT', id: documentId }],
            support: { documents: [doc], transactions: [] },
            origin: 'DOCUMENT',
            type: doc.documentType === 'OBLIGATION' ? TransactionType.SPEND : TransactionType.RECIEVED
          };
          
          const defaultEntries = applyAccountingRules(
            editDataForDoc,
            selectedCompany,
            accounts,
            rules,
            passwordIdToAccountKeyMap,
            creditCardIdToAccountKeyMap,
          );
          
          // Use amounts from defaultEntries (correct breakdown for this doc)
          // But use accounts from template (user's edited accounts)
          const entries = defaultEntries.entries.map((defaultEntry, index) => {
            const templateAccount = templateAccounts[index] || { accountKey: '', accountName: '' };
            
            // If template has an account, use it; otherwise use default
            const finalAccountKey = templateAccount.accountKey || defaultEntry.account?.key || '';
            const finalAccountName = templateAccount.accountName || defaultEntry.account?.name || '';
            
            return {
              ranking: defaultEntry.ranking,
              accountKey: finalAccountKey,
              accountName: finalAccountName,
              debit: parseFloat(defaultEntry.debit) || 0,
              credit: parseFloat(defaultEntry.credit) || 0,
            };
          });

          return {
            companyId: selectedCompany.id,
            description: draft?.description ?? '',
            stringDate: draft?.stringDate ?? moment().format('YYYY-MM-DD'),
            entryDate: draft?.entryDate ?? createEntryDate(draft?.stringDate ?? moment().format('YYYY-MM-DD')),
            entryType: 'emissionEntry',
            currency: currency,
            sources: [{ type: 'DOCUMENT', id: documentId }],
            // Also set documentId for backward compatibility (backend populates from sources too)
            documentId: documentId,
            entries: entries,
            reversed: false,
          };
        });

        const crossCompanyMsg = findCrossCompanySourceMessage(
          selectedCompany?.id,
          bulkDocuments || [],
          tAccounting,
        );
        if (crossCompanyMsg) {
          setErrorMessage(crossCompanyMsg);
          setIsLoading(false);
          return;
        }

        // Call GraphQL mutation with all entries
        const result = await createEntries({ input: entryInputs });

        if (result.error) {
          const errorMsg = result.error.graphQLErrors?.[0]?.message || result.error.message;
          throw new Error(errorMsg);
        }

        // Show success message with count
        dispatch(
          importAlertState({
            state: 'success',
            content: tAccounting('alerts.bulkCreatedContent', { count: bulkDocuments.length }),
            concept: tAccounting('alerts.bulkCreatedConcept'),
            duration: 4,
            time: new Date(),
          })
        );

        // Trigger list/detail refetch
        try {
          dispatch(setUpdateDocuments({ value: true }));
        } catch (e) {
          // Non-fatal: UI will still be correct when user navigates/reloads
        }
        
        // Refresh entries immediately after successful creation
        if (onEntryCreated) {
          onEntryCreated();
        }
        
        // Close modal on success
        onClose();
        return;
      }

      if (isFreeEntry && importDrafts && importDrafts.length > 0) {
        const draftsToCreate = importDrafts.map((draft, i) =>
          i === activeDraftIndex ? formDataToDraft(formData) : draft
        );

        const lockedDrafts = draftsToCreate.filter(
          (draft) => getLockForDate(draft.stringDate).isLocked
        );

        if (lockedDrafts.length > 0) {
          setErrorMessage(
            `Hay ${lockedDrafts.length} asiento(s) en período cerrado. Cambia la fecha o abre el período antes de crear.`
          );
          setIsLoading(false);
          return;
        }

        const invalidDrafts = draftsToCreate.filter(
          (draft) => !getDraftValidation(draft).isValid
        );

        if (invalidDrafts.length > 0) {
          setErrorMessage(
            `Hay ${invalidDrafts.length} asiento(s) desbalanceado(s) o incompleto(s). Corrígelos antes de crear.`
          );
          setIsLoading(false);
          return;
        }

        const entryInputs = draftsToCreate.map((draft) => ({
          companyId: selectedCompany.id,
          description: draft.description || '',
          stringDate: draft.stringDate,
          entryDate: createEntryDate(draft.stringDate),
          entryType: 'freeEntry',
          currency,
          entries: buildFreeEntryRowsForBackend(draft.entries),
          reversed: false,
        }));

        const result = await createEntries({ input: entryInputs });

        if (result.error) {
          const errorMsg = result.error.graphQLErrors?.[0]?.message || result.error.message;
          throw new Error(errorMsg);
        }

        const createdCount = result.data?.createEntries?.length ?? entryInputs.length;
        finishEntryCreateFlow(formData, {
          content: `${createdCount} asiento${createdCount === 1 ? '' : 's'} creado${createdCount === 1 ? '' : 's'} exitosamente.`,
          concept: 'Asientos creados',
          duration: 4,
        }, ruleSaveOfferContext);
        return;
      }

      // Regular (non-bulk) mode: create single entry
      // Prepare entries with account data and convert to numbers
      // When document amount is 0, include rows with accounts even if amounts are 0
      const existingEntryTotalMaxCreate =
        existingEntry && (existingEntry.entries || []).length > 0
          ? Math.max(
              (existingEntry.entries || []).reduce((s, r) => s + (r.debit ?? 0), 0),
              (existingEntry.entries || []).reduce((s, r) => s + (r.credit ?? 0), 0)
            )
          : 0;
      const isDuplicatingZeroAmountEmissionEntry =
        duplicateMode &&
        existingEntry &&
        formData.entryType === 'emissionEntry' &&
        existingEntryTotalMaxCreate < 0.01;

      const filterEntryRowForBackend = (entry) => {
        if (!entry.account) return false;
        const hasAmount = parseFloat(entry.debit) || parseFloat(entry.credit);
        return hasAmount || isEmissionEntryWithZeroAmount || isDuplicatingZeroAmountEmissionEntry;
      };

      const mapEntryRowForBackend = (entry) => {
        let accountKey = '';
        let accountName = '';
        if (typeof entry.account === 'string') {
          accountKey = entry.account;
          const accountObj = getAccountByKey(entry.account, accounts);
          accountName = accountObj?.name || entry.account;
        } else if (entry.account && typeof entry.account === 'object') {
          accountKey = entry.account.key || '';
          accountName = entry.account.name || '';
        }
        return {
          ranking: entry.ranking,
          accountKey,
          accountName,
          debit: parseFloat(entry.debit) || 0,
          credit: parseFloat(entry.credit) || 0,
          rut: entry.rut || undefined,
          businessName: entry.businessName || undefined,
        };
      };

      let entriesForBackend = formData.entries
        .filter(filterEntryRowForBackend)
        .map(mapEntryRowForBackend);

      if (showCostCentersFeature && ccPanelRef.current) {
        const assignment = ccPanelRef.current.getAssignmentState();
        const catalogList = ccPanelRef.current.getCatalogList?.() || [];
        try {
          const built = buildEntriesWithCostCenters(
            formData.entries,
            assignment,
            catalogList,
          );
          if (built.applyingCostCenters) {
            entriesForBackend = preserveAccountNames(built.entries, {
              accounts,
              fallbackLines: existingEntry?.entries,
            }).filter(
              (e) =>
                e.accountKey &&
                ((e.debit || e.credit) || isEmissionEntryWithZeroAmount || isDuplicatingZeroAmountEmissionEntry),
            );
          }
        } catch (ccErr) {
          setIsLoading(false);
          setErrorMessage(ccErr.message || tAccounting("journalEntry.costCenterError"));
          return;
        }
      }

      // Build sources array from formData sources or fallback to transactionId/documentId
      // Prefer explicit sources stored in formData, otherwise build from IDs
      let sources = formData.sources || [];
      
      // If no explicit sources, build from transactionId/documentId (backward compatibility)
      if (sources.length === 0) {
        if (formData.transactionId) {
          sources.push({ type: 'TRANSACTION', id: formData.transactionId });
        }
        if (formData.documentId) {
          sources.push({ type: 'DOCUMENT', id: formData.documentId });
        }
      }

      const crossCompanyMsg = findCrossCompanySourceMessage(
        selectedCompany?.id,
        [
          ...(editData?.support?.transactions || []),
          ...(editData?.support?.documents || []),
        ],
        tAccounting,
      );
      if (crossCompanyMsg) {
        setErrorMessage(crossCompanyMsg);
        setIsLoading(false);
        return;
      }

      // Prepare the entry data for GraphQL
      const entryInput = {
        companyId: selectedCompany.id,
        description: formData.description,
        stringDate: formData.stringDate,
        entryDate: {
          day: formData.entryDate.day,
          month: formData.entryDate.month,
          year: formData.entryDate.year,
          fullDate: formData.entryDate.fullDate,
        },
        entryType: formData.entryType,
        currency: currency,
        sources: sources.length > 0 ? sources : undefined,
        // Keep old fields for backward compatibility (backend will also populate from sources)
        transactionId: formData.transactionId,
        documentId: formData.documentId,
        matchId: formData.matchId,
        entries: entriesForBackend,
        reversed: reverseMode === true,
        reverseSourceEntryId: reverseMode
          ? (existingEntry?.id || existingEntry?._id || undefined)
          : undefined,
      };

      // Call GraphQL mutation
      const result = await createEntries({ input: [entryInput] });

      if (result.error) {
        const errorMsg = result.error.graphQLErrors?.[0]?.message || result.error.message;
        throw new Error(errorMsg);
      }

      // Show success message
      const newEntryNumber = result.data?.createEntries[0]?.entryNumber;
      const originSuffix = existingEntry?.entryNumber != null
        ? tAccounting('alerts.originSuffix', { number: existingEntry.entryNumber })
        : '';
      let successContent = tAccounting('alerts.createdContent', { number: newEntryNumber });
      let successConcept = tAccounting('alerts.createdConcept');
      if (reverseMode) {
        successContent = tAccounting('alerts.reversedContent', { number: newEntryNumber, origin: originSuffix });
        successConcept = tAccounting('alerts.reversedConcept');
      } else if (duplicateMode) {
        successContent = tAccounting('alerts.duplicatedContent', { number: newEntryNumber, origin: originSuffix });
        successConcept = tAccounting('alerts.duplicatedConcept');
      }

      // Trigger list/detail refetch so entryStatus updates in UI
      try {
        const touchedTypes = new Set((sources || []).map((s) => s?.type).filter(Boolean));
        if (touchedTypes.has('TRANSACTION')) {
          dispatch(importUpdateTransactions(true));
        }
        if (touchedTypes.has('DOCUMENT')) {
          dispatch(setUpdateDocuments({ value: true }));
        }
      } catch (e) {
        // Non-fatal: UI will still be correct when user navigates/reloads
      }

      finishEntryCreateFlow(formData, {
        content: successContent,
        concept: successConcept,
        duration: 4,
      }, ruleSaveOfferContext);
    } catch (err) {
      const errorMessage = err.message || 'Error inesperado. Por favor, intenta nuevamente.';
      const isPeriodLocked = errorMessage && typeof errorMessage === 'string' && 
        errorMessage.toLowerCase().includes('período') && 
        errorMessage.toLowerCase().includes('cerrado');
      
      // Show error message
      dispatch(
        importAlertState({
          state: 'error',
          content: errorMessage,
          concept: isPeriodLocked ? tAccounting('alerts.periodClosedConcept') : tAccounting('alerts.errorConcept'),
          duration: 4,
          time: new Date(),
        })
      );
      
      setErrorMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    const todayStringDate = moment().format('YYYY-MM-DD');
    const initialEntryDate = createEntryDate(todayStringDate);
    setFormData({
      description: '',
      stringDate: todayStringDate,
      entryDate: initialEntryDate,
      entryType: 'freeEntry',
      sources: [],
      transactionId: null,
      documentId: null,
      matchId: null,
      entries: [
        {
          ranking: 1,
          account: null,
          debit: '',
          credit: '',
        },
        {
          ranking: 2,
          account: null,
          debit: '',
          credit: '',
        }
      ],
    });
    setErrorMessage('');
    setIsLoading(false);
    setShowRcvDateMessage(false);
    setRcvPeriod(null);
    setImportDrafts(null);
    setActiveDraftIndex(0);
    onClose();
  };

  // Calculate totals (per active document in bulk mode, template totals otherwise)
  const bulkActiveDocumentPreview = useMemo(() => {
    if (!isBulkMode || !bulkDocuments?.length) return null;
    const doc = bulkDocuments[activeBulkDraftIndex];
    if (!doc || !selectedCompany) return null;

    const documentId = String(doc._id || doc.id);
    const editDataForDoc = {
      originType: doc.documentType,
      localAmount: resolveDocumentEntryAmountClp(doc),
      sources: [{ type: 'DOCUMENT', id: documentId }],
      support: { documents: [doc], transactions: [] },
      origin: 'DOCUMENT',
      type: doc.documentType === 'OBLIGATION' ? TransactionType.SPEND : TransactionType.RECIEVED,
    };

    const defaultEntries = applyAccountingRules(
      editDataForDoc,
      selectedCompany,
      accounts,
      rules,
      passwordIdToAccountKeyMap,
      creditCardIdToAccountKeyMap,
    );

    const previewEntries = (defaultEntries.entries || []).map((defaultEntry, rowIndex) => {
      const templateEntry = formData.entries[rowIndex];
      let accountKey = '';
      if (typeof templateEntry?.account === 'string') {
        accountKey = templateEntry.account;
      } else if (templateEntry?.account?.key) {
        accountKey = templateEntry.account.key;
      }
      return {
        debit: parseFloat(defaultEntry.debit) || 0,
        credit: parseFloat(defaultEntry.credit) || 0,
        accountKey: accountKey || defaultEntry.account?.key || '',
      };
    });

    const previewDebitTotal = previewEntries.reduce((sum, entry) => sum + entry.debit, 0);
    const previewCreditTotal = previewEntries.reduce((sum, entry) => sum + entry.credit, 0);

    return {
      debitTotal: previewDebitTotal,
      creditTotal: previewCreditTotal,
      expectedAmount: resolveDocumentEntryAmountClp(doc),
      isBalanced: Math.abs(previewDebitTotal - previewCreditTotal) < 0.01,
    };
  }, [
    isBulkMode,
    bulkDocuments,
    activeBulkDraftIndex,
    formData.entries,
    selectedCompany,
    accounts,
    rules,
    passwordIdToAccountKeyMap,
  ]);

  const templateDebitTotal = formData.entries.reduce((sum, entry) => {
    return sum + (parseFloat(entry.debit) || 0);
  }, 0);

  const templateCreditTotal = formData.entries.reduce((sum, entry) => {
    return sum + (parseFloat(entry.credit) || 0);
  }, 0);

  const debitTotal =
    isBulkMode && bulkActiveDocumentPreview
      ? bulkActiveDocumentPreview.debitTotal
      : templateDebitTotal;

  const creditTotal =
    isBulkMode && bulkActiveDocumentPreview
      ? bulkActiveDocumentPreview.creditTotal
      : templateCreditTotal;

  // Check if totals are balanced (within 0.01 tolerance for floating point precision)
  const isBalanced = Math.abs(debitTotal - creditTotal) < 0.01;
  const difference = Math.abs(debitTotal - creditTotal);

  // Get expected amount based on entry type
  const getExpectedAmount = () => {
    if (!editData) return null; // Free entry, no validation needed
    if (formData.entryType === 'freeEntry') return null;

    // In bulk mode, expected amount is for the active document (display/validation hints)
    if (isBulkMode && bulkDocuments && bulkDocuments.length > 0) {
      const doc = bulkDocuments[activeBulkDraftIndex];
      return doc ? resolveDocumentEntryAmountClp(doc) : null;
    }
    
    if (formData.entryType === 'matchEntry') {
      return editData.localAmount ?? null;
    } else if (formData.entryType === 'emissionEntry') {
      const transaction = editData.support?.transactions?.[0];
      const document = editData.support?.documents?.[0];
      
      if (transaction) {
        return resolveTransactionEntryAmountClp(transaction) || (editData.localAmount ?? null);
      } else if (document) {
        return (
          resolveDocumentEmissionSourceAmountClp(document) ||
          (editData.localAmount ?? null)
        );
      }
      
      return editData.localAmount ?? null;
    }
    
    return null;
  };

  const expectedAmount = getExpectedAmount();
  const emissionDocumentForCap =
    formData.entryType === 'emissionEntry' && !isBulkMode
      ? editData?.support?.documents?.[0]
      : null;
  const emissionJournalCap = emissionDocumentForCap
    ? resolveDocumentEmissionJournalCapClp(emissionDocumentForCap)
    : null;
  const emissionPayableAmount =
    emissionJournalCap != null &&
    emissionDocumentForCap &&
    isFacturaCompraWithIvaRetenido(emissionDocumentForCap)
      ? getDocumentPayableAmountClp(emissionDocumentForCap)
      : null;
  const emissionEntryTotal = Math.max(debitTotal, creditTotal);
  const editEntryType =
    resolvedExistingEntry?.entryType ?? formData.entryType ?? null;

  const showCostCentersFeature =
    isEditMode &&
    !isBulkMode &&
    !duplicateMode &&
    !reverseMode &&
    editEntryType !== 'matchEntry' &&
    (editEntryType === 'emissionEntry' || editEntryType === 'freeEntry');

  const ccHydrateEntryLines = useMemo(() => {
    if (!open || !isEditMode || reverseMode || !resolvedExistingEntry) return null;
    const lines = resolvedExistingEntry.entries || [];
    const hasCc =
      resolvedExistingEntry.hasCostCenters ||
      lines.some((r) => r.costCenters?.length);
    return hasCc ? lines : null;
  }, [open, isEditMode, reverseMode, resolvedExistingEntry]);

  useEffect(() => {
    if (!open || !ccHydrateEntryLines?.length) return;
    setCostCentersPanelOpen(true);
    costCentersPanelEngagedRef.current = true;
  }, [open, ccHydrateEntryLines]);

  const costCentersToggleLabel =
    assignedCcCount > 0
      ? `Centros de Costos Asignados (${assignedCcCount})`
      : tAccounting("journalEntry.assignCostCenter");

  const isEmissionEntryWithZeroAmount =
    formData.entryType === 'emissionEntry' &&
    expectedAmount !== null &&
    Math.abs(expectedAmount) < 0.01;

  const existingEntryTotalMax =
    existingEntry && (existingEntry.entries || []).length > 0
      ? Math.max(
          (existingEntry.entries || []).reduce((s, r) => s + (r.debit ?? 0), 0),
          (existingEntry.entries || []).reduce((s, r) => s + (r.credit ?? 0), 0)
        )
      : 0;
  const isEditingZeroAmountEmissionEntry =
    (isEditMode || duplicateMode) &&
    existingEntry &&
    formData.entryType === 'emissionEntry' &&
    existingEntryTotalMax < 0.01;

  // Validation checks:
  // 1. Check if all entries with amounts have accounts
  // Helper function to check if a value represents a non-zero amount
  const hasAmount = (value) => {
    if (!value || value === '' || value === '0') return false;
    const num = parseFloat(value);
    return !isNaN(num) && num > 0;
  };
  
  const hasEmptyAccounts = formData.entries.some(entry => 
    (hasAmount(entry.debit) || hasAmount(entry.credit)) && !entry.account
  );

  // 2. Check if Debe and Haber match (they must be equal)
  const amountsMatch = isBalanced;

  // 3. Check if totals match expected amount (only if not free entry and expected amount exists)
  // In bulk mode or regular mode, both debe and haber should equal the expected amount
  // Since debe and haber are equal (isBalanced), we just need to check if they match expected
  // Use expectedAmount == null to allow 0 as valid expected amount (e.g. zero-amount documents)
  const amountMatchesExpected =
    !editData ||
    expectedAmount == null ||
    (isBalanced &&
      (formData.entryType === 'matchEntry'
        ? // matchEntry: se permite igual o mayor al monto del match; solo se rechaza si
          // queda por debajo más allá del epsilon (0.01).
          expectedAmount - debitTotal <= 0.01
        : emissionJournalCap != null
          ? Math.abs(emissionEntryTotal - emissionJournalCap) < 0.01
          : Math.abs(debitTotal - expectedAmount) < 0.01 &&
            Math.abs(creditTotal - expectedAmount) < 0.01));

  // Aviso informativo (no bloqueante): el asiento de match se contabiliza por sobre el monto del match.
  const matchAmountAboveExpected =
    formData.entryType === 'matchEntry' &&
    expectedAmount != null &&
    isBalanced &&
    debitTotal - expectedAmount > 0.01;
  const emissionAmountHintMismatch =
    emissionJournalCap != null &&
    isBalanced &&
    !amountMatchesExpected;
  const emissionAmountHintEqualityMismatch =
    emissionJournalCap == null &&
    expectedAmount != null &&
    isBalanced &&
    !amountMatchesExpected;

  const hasNonZeroAmounts = formData.entries.some(
    (entry) => entry.account && (hasAmount(entry.debit) || hasAmount(entry.credit))
  );
  const hasValidZeroEmission =
    (isEmissionEntryWithZeroAmount || isEditingZeroAmountEmissionEntry) &&
    formData.entries.filter((e) => e.account).length >= 2 &&
    Math.abs(debitTotal) < 0.01 &&
    Math.abs(creditTotal) < 0.01;

  const importDraftsWithActive = importDrafts
    ? importDrafts.map((draft, i) =>
        i === activeDraftIndex ? formDataToDraft(formData) : draft
      )
    : null;

  const invalidImportDraftCount = importDraftsWithActive
    ? importDraftsWithActive.filter((d) => !getDraftValidation(d).isValid).length
    : 0;

  const lockedImportDraftCount = importDraftsWithActive
    ? importDraftsWithActive.filter((d) => getLockForDate(d.stringDate).isLocked).length
    : 0;

  const lockedBulkDraftCount = isBulkMode && bulkDocumentDrafts?.length
    ? bulkDocumentDrafts.filter((d) => getBulkLockForDate(d.stringDate).isLocked).length
    : 0;

  const allImportDraftsValid = importDraftsWithActive
    ? invalidImportDraftCount === 0 && lockedImportDraftCount === 0
    : true;

  const isValidJournalEntryDate = (value) =>
    Boolean(value && moment(value, 'YYYY-MM-DD', true).isValid());

  const hasValidEntryDate = isBulkMode
    ? (bulkDocumentDrafts?.length
        ? bulkDocumentDrafts.every((d) => isValidJournalEntryDate(d.stringDate))
        : true)
    : isValidJournalEntryDate(formData.stringDate);

  const isFormValid = importDrafts
    ? allImportDraftsValid
    : (hasNonZeroAmounts || hasValidZeroEmission) &&
      !hasEmptyAccounts &&
      (isBulkMode
        ? (bulkActiveDocumentPreview?.isBalanced ?? amountsMatch)
        : amountsMatch) &&
      amountMatchesExpected &&
      hasValidEntryDate;

  const checkingAnyPeriodLock = importDrafts?.length
    ? checkingDraftPeriodLocks
    : isBulkMode && bulkDocumentDrafts?.length
      ? checkingBulkPeriodLocks
      : checkingPeriodLock;

  const bulkPeriodLocked = isBulkMode && lockedBulkDraftCount > 0;

  const activeBulkDraft =
    isBulkMode && bulkDocumentDrafts.length > 0
      ? bulkDocumentDrafts[activeBulkDraftIndex] ?? bulkDocumentDrafts[0]
      : null;

  const activeBulkPeriodLock = activeBulkDraft
    ? getBulkLockForDate(activeBulkDraft.stringDate)
    : { isLocked: false, message: '' };

  // Check if origin is MATCH (from MatchRow) to show ReconcileDocumentSideBar
  // This happens when clicking contabilizar icon on a document/obligation match
  // For matches, we want to show the match (obligation) information, not the original document
  const isMatch = editData?.origin === 'MATCH';
  const showReconcileDocumentSideBar = isMatch && editData?.support?.documents && editData?.support?.documents.length > 0;
  
  // Check if origin is DOCUMENT (from reconcileContabilizar when isDocument == true)
  // Show DocumentInfoBar for the origin document (not from a match)
  const isDocumentOrigin = editData?.origin === 'DOCUMENT' && !isMatch;
  const showDocumentInfoBar = isDocumentOrigin && editData?.support?.documents && editData?.support?.documents.length > 0;
  const originDocument = showDocumentInfoBar ? editData.support.documents[0] : null;
  
  // For matches, determine which document is the match (the one that was clicked)
  // In MatchRow, originType is set from match.documentType, so the match is the one matching originType
  // The documents array structure: [editData (if document), match]
  // So if there are 2 documents, match is the second one; if 1, it's the match itself
  let matchDocument = null;
  if (isMatch && editData?.support?.documents?.length > 0) {
    if (editData.support.documents.length > 1) {
      // Multiple documents: find the one that matches originType (the match)
      // originType in MatchRow comes from match.documentType, so find by that
      matchDocument = editData.support.documents.find(doc => 
        doc.documentType === editData.originType
      ) || editData.support.documents[editData.support.documents.length - 1]; // Fallback to last (the match)
    } else {
      // Only one document - it's the match
      matchDocument = editData.support.documents[0];
    }
  }

  const handleCcSaveConfirmProceed = () => {
    skipCcSaveGateRef.current = true;
    setCcSaveConfirm(null);
    handleConfirm();
  };

  return (
    <>
    <CustomModal
      open={open}
      onClose={handleCancel}
      title={
        reverseMode
          ? t('journalEntryModal.titleReverse')
          : duplicateMode
            ? t('journalEntryModal.titleCopy')
            : isEditMode
              ? t('journalEntryModal.titleEdit')
              : t('journalEntryModal.titleNew')
      }
      classNameModal={['create-journal-entry-modal', modalClassName].filter(Boolean).join(' ')}
      content={
        <div className="create-journal-entry-modal-content">
          {errorMessage && (
            <div className="journal-entry-error-message" style={{ 
              backgroundColor: 'var(--salmon-pale)', 
              color: 'var(--salmon-dark)', 
              padding: '12px', 
              borderRadius: '4px', 
              marginBottom: '20px',
              border: '1px solid #ffcdd2'
            }}>
              <Icon name="exclamation triangle" color="red" />
              {errorMessage}
            </div>
          )}
          {isPeriodLocked && (
            <div className="journal-entry-period-locked-message" style={{ 
              backgroundColor: '#fff3e0', 
              color: '#e65100', 
              padding: '12px', 
              borderRadius: '4px', 
              marginBottom: '20px',
              border: '1px solid #ffcc80',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <svg
                className="journal-entry-period-locked-icon"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                aria-hidden="true"
              >
                <rect x="3" y="7" width="10" height="7.5" rx="1.5" fill="currentColor" />
                <path
                  d="M5.25 7V5.1a2.75 2.75 0 0 1 5.5 0V7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              <span>
                <strong>{t('journalEntryModal.periodClosed')}</strong> {periodLockMessage || t('journalEntryModal.periodLockedDefault')}
              </span>
            </div>
          )}
          {isEditMode && honorarioEditSide && (
            <div className="journal-entry-honorario-message" style={{
              backgroundColor: '#ecfdf5',
              color: '#047857',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '20px',
              border: '1px solid #a7f3d0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Icon name="info circle" color="green" />
              <span>
                {honorarioEditSide === 'retencion'
                  ? t('journalEntryModal.honorarioRetencionNotice')
                  : t('journalEntryModal.honorarioNotice')}
              </span>
            </div>
          )}
          <div className="journal-entry-modal-sections">
            <div className="journal-entry-left-section">
              {/* Show import/export component for free entries */}
              {isFreeEntry && (
                <>
                  <JournalEntryImportExport onImport={handleImportEntries} accounts={accounts} />
                  {importDrafts && importDrafts.length > 0 && (
                    <div className="journal-entry-import-summary">
                      <p className="journal-entry-import-summary__title">
                        {importDrafts.length === 1
                          ? '1 asiento importado desde Excel'
                          : `${importDrafts.length} asientos importados desde Excel`}
                        {invalidImportDraftCount > 0 && (
                          <span className="journal-entry-import-summary__warn">
                            {' '}
                            · {invalidImportDraftCount} con errores
                          </span>
                        )}
                        {lockedImportDraftCount > 0 && (
                          <span className="journal-entry-import-summary__warn">
                            {' '}
                            · {lockedImportDraftCount} en período cerrado
                          </span>
                        )}
                        {checkingDraftPeriodLocks && (
                          <span className="journal-entry-import-summary__meta">
                            {' '}
                            · verificando períodos…
                          </span>
                        )}
                      </p>
                      {importDrafts.length > 1 && (
                        <JournalEntryDraftTabs
                          drafts={importDraftsWithActive}
                          activeIndex={activeDraftIndex}
                          onSelect={handleSelectDraft}
                          getPeriodLock={getLockForDate}
                          getTabLabel={(_, index) => `Asiento ${index + 1}`}
                          getValidation={getDraftValidation}
                        />
                      )}
                    </div>
                  )}
                  <div className="journal-entry-divider"></div>
                </>
              )}

              {/* Show DocumentInfoBar when origin is DOCUMENT (from reconcileContabilizar) - Hide in bulk mode */}
              {showDocumentInfoBar && originDocument && !isBulkMode && (
                <div className="journal-entry-reconcile-sidebar-wrapper">
                  <DocumentInfoBar editData={originDocument} />
                </div>
              )}
              
              {/* Show ReconcileDocumentSideBar when origin is MATCH (document/obligation) */}
              {/* Show the match document (obligation), not the original document */}
              {showReconcileDocumentSideBar && matchDocument && (
                <div className="journal-entry-reconcile-sidebar-wrapper">
                  <ReconcileDocumentSideBar
                    hideActionsAndMatches={true}
                    hideReportButton={true}
                    documentData={matchDocument}
                  />
                </div>
              )}
              
              {/* Bulk Mode Indicator */}
              {isBulkMode && (
                <InfoBanner
                  title={t('journalEntryModal.bulkTitle', { count: bulkDocuments.length })}
                  description={t('journalEntryModal.bulkDescription', { count: bulkDocuments.length })}
                />
              )}

              {/* Bulk mode: tabs to switch between documents (same pattern as Excel import) */}
              {isBulkMode && bulkDocumentDrafts.length > 0 && (
                <div className="journal-entry-import-summary">
                  {bulkDocumentDrafts.length > 1 && (
                    <>
                      <p className="journal-entry-import-summary__title">
                        {t('journalEntryModal.bulkPerDocumentTitle')}
                        {lockedBulkDraftCount > 0 && (
                          <span className="journal-entry-import-summary__warn">
                            {' '}
                            · {t('journalEntryModal.bulkPeriodLockedWarning', { count: lockedBulkDraftCount })}
                          </span>
                        )}
                        {checkingBulkPeriodLocks && (
                          <span className="journal-entry-import-summary__meta">
                            {' '}
                            · {t('journalEntryModal.bulkCheckingPeriods')}
                          </span>
                        )}
                      </p>
                      <JournalEntryDraftTabs
                        drafts={bulkDocumentDrafts}
                        activeIndex={activeBulkDraftIndex}
                        onSelect={handleSelectBulkDraft}
                        getPeriodLock={getBulkLockForDate}
                        getTabLabel={(draft, index) =>
                          draft.label ||
                          t('journalEntryModal.bulkDocumentLabel', { index: index + 1 })
                        }
                        truncateLabel
                      />
                    </>
                  )}
                  {bulkDocumentDrafts.length === 1 && lockedBulkDraftCount > 0 && (
                    <p className="journal-entry-import-summary__title">
                      <span className="journal-entry-import-summary__warn">
                        {t('journalEntryModal.bulkPeriodLockedWarning', { count: lockedBulkDraftCount })}
                      </span>
                    </p>
                  )}
                  <div className="journal-entry-divider" />
                </div>
              )}

              {/* Glosa and Fecha for active bulk document */}
              {isBulkMode && activeBulkDraft && (
                <>
                  {bulkDocumentDrafts.length === 1 && (
                    <p className="journal-entry-bulk-active-doc" title={activeBulkDraft.label}>
                      {activeBulkDraft.label}
                    </p>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                    <div className="journal-entry-field">
                      <label className="journal-entry-label" htmlFor="bulk-description-active">
                        {t('journalEntryModal.glossOptional')}
                      </label>
                      <input
                        id="bulk-description-active"
                        type="text"
                        value={activeBulkDraft.description}
                        onChange={(e) => handleBulkDraftDescriptionChange(e.target.value)}
                        placeholder={t('journalEntryModal.glossPlaceholder')}
                      />
                    </div>
                    <div className="journal-entry-field">
                      <label className="journal-entry-label" htmlFor="bulk-date-active">
                        {t('journalEntryModal.entryDate')}
                      </label>
                      <input
                        id="bulk-date-active"
                        type="date"
                        value={stringDateToDateInputValue(activeBulkDraft.stringDate)}
                        onChange={handleBulkDraftDateChange}
                      />
                      {activeBulkDraft.rcvPeriod && (
                        <p className="journal-entry-bulk-rcv-hint">
                          {t('journalEntryModal.rcvDateHint', {
                            period: activeBulkDraft.rcvPeriod.length === 6
                              ? `${activeBulkDraft.rcvPeriod.substring(0, 4)}-${activeBulkDraft.rcvPeriod.substring(4, 6)}`
                              : activeBulkDraft.rcvPeriod,
                          })}
                        </p>
                      )}
                      {activeBulkPeriodLock.isLocked && (
                        <p className="journal-entry-bulk-lock-hint">
                          {activeBulkPeriodLock.message || t('journalEntryModal.periodLockedDefault')}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Glosa and Fecha del asiento in same row (single-doc mode) */}
              {!isBulkMode && (
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                <div className="journal-entry-field">
                  <label className="journal-entry-label" htmlFor="description">{t('journalEntryModal.glossOptional')}</label>
                  <input
                    id="description"
                    type="text"
                    value={formData.description}
                    onChange={handleDescriptionChange}
                    placeholder={t('journalEntryModal.glossPlaceholder')}
                    readOnly={reverseMode}
                  />
                </div>
                <div className="journal-entry-field">
                  <label className="journal-entry-label" htmlFor="contabilizar-para">{t('journalEntryModal.entryDate')}</label>
                  <input
                    id="contabilizar-para"
                    type="date"
                    value={stringDateToDateInputValue(formData.stringDate)}
                    onChange={handleDateChange}
                  />
                  {showRcvDateMessage && rcvPeriod && (
                    <p style={{ 
                      fontSize: '12px', 
                      color: 'var(--text-mid)', 
                      marginTop: '4px', 
                      marginBottom: 0,
                      fontStyle: 'italic'
                    }}>
                      {t('journalEntryModal.rcvDateHint', {
                        period: rcvPeriod.length === 6 ? `${rcvPeriod.substring(0, 4)}-${rcvPeriod.substring(4, 6)}` : rcvPeriod,
                      })}
                    </p>
                  )}
                </div>
              </div>
              )}

              {/* Horizontal divider */}
              <div className="journal-entry-divider"></div>

              {/* Debit/Credit Totals */}
              <div className={`journal-entry-totals ${isFormValid ? 'journal-entry-totals--balanced' : 'journal-entry-totals--unbalanced'}`}>
                <div className="journal-entry-totals-left">
                  <p className="journal-entry-totals-text">
                    {isBulkMode && bulkActiveDocumentPreview && (
                      <span className="journal-entry-bulk-totals-hint">
                        {t('journalEntryModal.bulkActiveDocumentTotals')}
                        {' · '}
                      </span>
                    )}
                    {t('journalEntryModal.debitTotal', { amount: formatMoneyWithCurrency(debitTotal) })}
                    {' | '}
                    {t('journalEntryModal.creditTotal', { amount: formatMoneyWithCurrency(creditTotal) })}
                    {!isBalanced && ` | ${t('journalEntryModal.difference', { amount: formatMoneyWithCurrency(difference) })}`}
                    {hasEmptyAccounts && (
                      <span style={{ color: 'var(--salmon-dark)', marginLeft: '8px' }}>
                        {' | '}{t('journalEntryModal.missingAccounts')}
                      </span>
                    )}
                    {!hasEmptyAccounts && emissionAmountHintMismatch && emissionJournalCap != null ? (
                      <span style={{ color: 'var(--salmon-dark)', marginLeft: '8px' }}>
                        {' | '}{t('journalEntryModal.emissionJournalTotal', { amount: formatMoneyWithCurrency(emissionJournalCap) })}
                        {emissionPayableAmount != null
                          ? ` | ${t('journalEntryModal.emissionPayableCredit', { amount: formatMoneyWithCurrency(emissionPayableAmount) })}`
                          : ''}
                      </span>
                    ) : null}
                    {!hasEmptyAccounts && emissionAmountHintEqualityMismatch ? (
                      <span style={{ color: 'var(--salmon-dark)', marginLeft: '8px' }}>
                        {' | '}{t('journalEntryModal.expectedAmount', { amount: formatMoneyWithCurrency(expectedAmount) })}
                      </span>
                    ) : null}
                    {!hasEmptyAccounts && matchAmountAboveExpected ? (
                      <span style={{ color: 'var(--text-secondary, #6b7280)', marginLeft: '8px' }}>
                        {' | '}{t('journalEntryModal.matchAmountAbove', { amount: formatMoneyWithCurrency(expectedAmount) })}
                      </span>
                    ) : null}
                  </p>
                  <Icon
                    name={isFormValid ? 'check circle' : 'exclamation circle'}
                    className="journal-entry-totals-icon"
                  />
                </div>
                <div className="journal-entry-totals-actions">
                    {showCostCentersFeature && (
                      <button
                        type="button"
                        className={`journal-entry-cc-toggle-button${costCentersPanelOpen ? ' journal-entry-cc-toggle-button--open' : ''}`}
                        onClick={() => setCostCentersPanelOpen((v) => !v)}
                        aria-expanded={costCentersPanelOpen}
                        aria-controls="journal-entry-cost-centers-panel"
                      >
                        <span>{costCentersToggleLabel}</span>
                        <Icon name={costCentersPanelOpen ? 'chevron up' : 'chevron down'} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="journal-entry-invert-button"
                      onClick={handleInvertEntryAmounts}
                      title={tAccounting("journalEntry.invertEntriesTitle")}
                      aria-label={tAccounting("journalEntry.invertEntriesAria")}
                    >
                      <span>{tAccounting("journalEntry.invertEntries")}</span>
                      <Icon name="exchange" />
                    </button>
                  </div>
              </div>

              {showCostCentersFeature && (
                <div
                  id="journal-entry-cost-centers-panel"
                  className={`journal-entry-cc-accordion${costCentersPanelOpen ? ' journal-entry-cc-accordion--expanded' : ''}`}
                >
                  <JournalEntryCostCentersPanel
                    ref={ccPanelRef}
                    key={costCentersPanelKey}
                    journalEntries={formData.entries}
                    companyId={selectedCompany?.id}
                    panelOpen={costCentersPanelOpen}
                    hydrateEntryId={isEditMode ? editEntryId : null}
                    hydrateEntryLines={ccHydrateEntryLines}
                    onAssignmentChange={setAssignedCcCount}
                    onDiscardAssignment={() => {
                      costCentersPanelEngagedRef.current = false;
                      setCostCentersPanelOpen(false);
                      setAssignedCcCount(0);
                    }}
                  />
                </div>
              )}

              {/* Journal Entry Rows */}
              <div className="journal-entry-rows-container">
                {formData.entries.map((entry, index) => (
                  <JournalEntryRow
                    key={entry.ranking}
                    account={entry.account}
                    debit={entry.debit}
                    credit={entry.credit}
                    onAccountChange={(account) => handleEntryAccountChange(entry.ranking, account)}
                    onDebitChange={(debit) => handleEntryDebitChange(entry.ranking, debit)}
                    onCreditChange={(credit) => handleEntryCreditChange(entry.ranking, credit)}
                    onRemove={!reverseMode && formData.entries.length > 2 ? () => handleRemoveEntry(entry.ranking) : null}
                    showRemoveButton={!reverseMode && formData.entries.length > 2}
                    amountsReadOnly={isBulkMode}
                    disabled={reverseMode}
                    accountPlaceholder={t('journalEntryModal.selectAccount')}
                  />
                ))}
                {!reverseMode && (
                <AddDashedOutlineButton onClick={handleAddEntry}>
                  {t('journalEntryModal.addAccount')}
                </AddDashedOutlineButton>
                )}
              </div>
            </div>
          </div>
        </div>
      }
      cancelarLabel={t('common.cancel')}
      confirmLabel={
        isEditMode || duplicateMode || reverseMode
          ? t('common.save')
          : isBulkMode && bulkDocuments?.length > 1
            ? t('journalEntryModal.bulkConfirmLabel', { count: bulkDocuments.length })
            : importDrafts?.length > 1
              ? t('journalEntryModal.createEntries', { count: importDrafts.length })
              : t('journalEntryModal.createEntry')
      }
      onConfirm={handleConfirm}
      confirmDisabled={
        !isFormValid || isLoading || checkingAnyPeriodLock ||
        (importDrafts?.length ? lockedImportDraftCount > 0 : bulkPeriodLocked || (!isBulkMode && isPeriodLocked))
      }
      isLoading={isLoading}
      size="tiny"
      classNameTitle="!tw-text-center !tw-text-[16px] !tw-font-semibold"
      classNameContent="create-journal-entry-modal-content-wrapper"
      dimmer={{ style: { zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' } }}
      modalStyle={{ 
        maxWidth: '95%', 
        minWidth: '1300px', 
        minHeight: '85vh',
        maxHeight: '90vh', 
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column'
      }}
    />
    {ccSaveConfirm && (
      <CustomModal
        open
        onClose={() => setCcSaveConfirm(null)}
        title={tAccounting("journalEntry.costCenters")}
        size="tiny"
        classNameModal="journal-entry-cc-save-confirm-modal"
        classNameContent="journal-entry-cc-save-confirm-modal__content"
        dimmer={{ style: { zIndex: 10080, backgroundColor: 'rgba(0,0,0,0.45)' } }}
        modalStyle={{ zIndex: 10081 }}
        content={<p className="journal-entry-cc-save-confirm-modal__text">{ccSaveConfirm.message}</p>}
        confirmLabel={ccSaveConfirm.type === 'confirm' ? tAccounting("journalEntry.saveWithoutCenters") : undefined}
        cancelarLabel={ccSaveConfirm.type === 'confirm' ? tAccounting("journalEntry.completeAssignment") : undefined}
        onConfirm={
          ccSaveConfirm.type === 'confirm' ? handleCcSaveConfirmProceed : undefined
        }
        customActions={
          ccSaveConfirm.type === 'blocking' ? (
            <div className="journal-entry-cc-save-confirm-modal__actions">
              <button
                type="button"
                className="ui primary button"
                onClick={() => setCcSaveConfirm(null)}
              >
                Volver
              </button>
            </div>
          ) : undefined
        }
      />
    )}
    </>
  );
};

export default CreateJournalEntryModal;

